/**
 * PURPOSE: Persistir y normalizar el zoom visual local del scoreboard HDMI de HandballBeach.
 * RESPONSIBILITIES:
 *  - Sí: definir límites, paso, valor por defecto y key de localStorage.
 *  - Sí: leer/escribir un zoom interno del scoreboard sin usar zoom del navegador.
 *  - No: no toca BroadcastChannel, DOM fullscreen ni lógica deportiva.
 * COLLABORATORS:
 *  - hooks/useHandballBeachScoreboardReceiver.js
 *  - FullScreenPlanillaHandballBeach.jsx
 * NOTES: localStorage comparte origen entre planilla y /handballBeach-scoreboard; esto permite persistir ajuste por TV/proyector.
 * ROUTE: src/components/FullScreenModal/FullScreenPlanilla/HandballBeach/utils/handballBeachScoreboardZoomStorage.js
 */

export const HANDBALL_BEACH_SCOREBOARD_ZOOM = Object.freeze({
  KEY: "winscore_handballBeach_scoreboard_zoom_v1",
  DEFAULT: 1,
  MIN: 0.7,
  MAX: 1.1,
  STEP: 0.05,
});

// Redondea a 2 decimales para evitar residuos flotantes tipo 0.8500000001.
function roundZoom(value) {
  return Math.round(Number(value) * 100) / 100;
}

// Normaliza cualquier entrada a un zoom seguro para TV.
export function clampHandballBeachScoreboardZoom(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return HANDBALL_BEACH_SCOREBOARD_ZOOM.DEFAULT;
  return roundZoom(Math.min(HANDBALL_BEACH_SCOREBOARD_ZOOM.MAX, Math.max(HANDBALL_BEACH_SCOREBOARD_ZOOM.MIN, n)));
}

// Lee el zoom persistido; si no existe o es inválido, usa default TV-safe.
export function readHandballBeachScoreboardZoom() {
  if (typeof window === "undefined") return HANDBALL_BEACH_SCOREBOARD_ZOOM.DEFAULT;

  try {
    const raw = window.localStorage.getItem(HANDBALL_BEACH_SCOREBOARD_ZOOM.KEY);
    if (raw == null || raw === "") return HANDBALL_BEACH_SCOREBOARD_ZOOM.DEFAULT;
    return clampHandballBeachScoreboardZoom(Number(raw));
  } catch (err) {
    return HANDBALL_BEACH_SCOREBOARD_ZOOM.DEFAULT;
  }
}

// Persiste el zoom normalizado y devuelve el valor final aplicado.
export function writeHandballBeachScoreboardZoom(value) {
  const next = clampHandballBeachScoreboardZoom(value);
  if (typeof window === "undefined") return next;

  try {
    window.localStorage.setItem(HANDBALL_BEACH_SCOREBOARD_ZOOM.KEY, String(next));
  } catch (err) {
    // Persistir zoom nunca debe romper la operación de la planilla.
  }

  return next;
}

// Suma/resta un paso respetando límites.
export function stepHandballBeachScoreboardZoom(current, direction) {
  const sign = direction === "in" ? 1 : -1;
  return clampHandballBeachScoreboardZoom(Number(current) + sign * HANDBALL_BEACH_SCOREBOARD_ZOOM.STEP);
}

// Convierte 0.9 -> "90%" para UI de mesa.
export function formatHandballBeachScoreboardZoomPercent(value) {
  return `${Math.round(clampHandballBeachScoreboardZoom(value) * 100)}%`;
}
